import type { Pair } from '../messages';

/**
 * Client-side network failures — these strings are the app's own, so both languages are used.
 */
export const errors = {
  'errors.offline': ['Нет связи с сервером. Проверьте подключение.', 'No connection to the server. Check your network.'],
  'errors.unknown': ['Что-то пошло не так.', 'Something went wrong.'],
} satisfies Record<string, Pair>;

/**
 * Server error codes. Russian keeps the string the server wrote (that is what users see today, and
 * the dictionary must not silently re-edit it); English maps the stable code instead. `messageOf()`
 * consults this table only when the locale is `en`.
 */
export const errcodes = {
  'err.UNAUTHORIZED': ['Нужно войти в аккаунт.', 'Sign in to continue.'],
  'err.FORBIDDEN': ['Недостаточно прав для этого действия.', 'You don’t have access to this.'],
  'err.NOT_FOUND': ['Ничего не найдено.', 'Nothing found here.'],
  'err.CONFLICT': ['Запись уже существует.', 'That entry already exists.'],
  'err.ALREADY_EXISTS': ['Такая запись уже существует.', 'That entry already exists.'],
  'err.ITEM_DUPLICATE': ['Эта ссылка уже есть в вашей библиотеке.', 'This link is already in your library.'],
  'err.INVALID_CREDENTIALS': ['Неверный email или пароль.', 'Wrong email or password.'],
  'err.EMAIL_TAKEN': ['Этот email уже зарегистрирован.', 'That email is already registered.'],
  'err.CSRF_MISMATCH': ['Сессия устарела. Обновите страницу.', 'Your session expired. Reload the page.'],
  'err.VALIDATION': ['Проверьте введённые данные.', 'Check what you entered.'],
  'err.BAD_REQUEST': ['Запрос неверный.', 'That request was not valid.'],
  'err.BAD_JSON': ['Некорректный формат запроса.', 'Malformed request body.'],
  'err.TOO_MANY_ATTEMPTS': [
    'Слишком много попыток. Подождите.',
    'Too many attempts. Please wait a minute.',
  ],
  'err.USERNAME_TAKEN': ['Это имя пользователя уже занято.', 'That username is already taken.'],
  'err.INTERNAL': ['Сервер не ответил.', 'The server did not respond.'],
} satisfies Record<string, Pair>;

/** Reasons behind a zod field issue; `messageOf()` composes `field: reason` for English. */
export const zodcodes = {
  'err.zod.too_small': ['Поле слишком короткое.', 'too short'],
  'err.zod.too_big': ['Поле слишком длинное.', 'too long'],
  'err.zod.invalid_type': ['Поле не заполнено.', 'required'],
  'err.zod.invalid_value': ['Значение неверно.', 'invalid'],
  'err.zod.invalid_format': ['Формат неверный.', 'wrong format'],
  'err.zod.custom': ['Поле заполнено неверно.', 'not valid'],
} satisfies Record<string, Pair>;

/** Field slugs the API reports; only English needs them spelled out. */
export const fields = {
  'field.identifier': ['учётные данные', 'email or username'],
  'field.email': ['email', 'email'],
  'field.password': ['пароль', 'password'],
  'field.displayName': ['имя', 'display name'],
  'field.username': ['имя пользователя', 'username'],
  'field.bio': ['описание профиля', 'bio'],
  'field.avatarUrl': ['ссылка на аватар', 'avatar link'],
  'field.url': ['ссылка', 'link'],
  'field.title': ['название', 'title'],
  'field.description': ['описание', 'description'],
  'field.imageUrl': ['ссылка на картинку', 'image link'],
  'field.coverImageUrl': ['обложка', 'cover link'],
  'field.author': ['автор', 'author'],
  'field.personalNote': ['личная заметка', 'note'],
  'field.blocks': ['порядок блоков', 'block order'],
  'field.content': ['текст блока', 'block text'],
  'field.currentPassword': ['текущий пароль', 'current password'],
  'field.newPassword': ['новый пароль', 'new password'],
  'field.itemId': ['элемент библиотеки', 'library item'],
  'field.position': ['позиция', 'position'],
  'field.type': ['тип', 'type'],
  'field.visibility': ['видимость', 'visibility'],
  'field.name': ['название подборки', 'collection name'],
  'field.note': ['комментарий', 'comment'],
} satisfies Record<string, Pair>;
